"use strict";

var _ = require('underscore');
var Backbone = require('dibs-backbone');
var fieldRegex  = /\[\d+\]/g;
var ErrorList = require('bunsen/validation/deprecated/helpers/ErrorList');
var FieldValidation = require('bunsen/validation/deprecated/field-validations/FieldValidation');
var DEFAULT_MESSAGE = 'Please correct this value : ';
var DEFAULTREQUIREDMESSAGE = 'This field is required';
var getDeferredMethods = require('bunsen/helpers/getDeferredMethods');
var assert = require('bunsen/helpers/assert');

var when = getDeferredMethods().when;

/**
 * @class Validator
 *
 * Validator Class constructor follows backbone JS style inheritance. An instance of validator class *requires that you
 * specify a modelConstructor, and a validationRules set. You may also add an initialize method, and of course, any
 * other public and private methods you choose to add.
 *
 *
 * Default methods / properties :
 *      - initialize - function
 *      - fieldValidations - object | function **
 *      - modelConstructor - constructor function
 *      - validate - function ***
 *
 * @property {string} EVENT_WILL_VALIDATE_FIELD validator:willValidateField
 * @property {string} EVENT_FIELD_PASSED_VALIDATION validator:fieldPassedValidation
 * @property {string} EVENT_FIELD_FAILED_VALIDATION validator:fieldFailedValidation
 * @property {string} EVENT_FIELD_FIXED_VALIDATION validator:fieldFixedValidation
 *
 * @example
 * // ** fieldValidations may be an object with the validation rules in the expected format, OR a function that returns such
 * // a rules set. Validator rules follow this format :
 *      {
 *          // each value must be {@link FieldValidation} or
 *          // extension instance
 *          key : new FieldValidation({
 *              field: 'foo', // model attribute key
 *              required: true, // make this field required
 *              fieldDisplayName: 'Foo', // combined with rule messageBody values to make a message ('"Foo" field must be bar')
 *              additionalRules: {  // Specify an object of additional rules not defined by the FieldValidation class
 *                  bar: new FieldRule({
 *                      messageBody: 'must be bar',
 *                      validate: function (model, value, fieldName) {
  *                         return value === 'bar';
  *                     }
 *                  })
 *              }
 *          })
 *      }
 *
 * @example
 * // *** By default the validate function invokes the validation logic. If you want to over-ride the validation function
 * // to do some setup then you should invoke the parent's validation method like so :
 * var EntityValidator = Validator.extend({
 *      validate : function (options) {
 *          // do something...
 *          return EntityValidator.__super__.validate.apply(this, arguments);
 *      }
 * });
 *
 *
 */
var Validator = function () {
    var self = this;
    /**
     * @memberOf Validator#
     * @private
     */
    this._childValidators = {};

    this.initialize.apply(this, arguments);
    when(this.getReadyPromise()).done(function () {
        /**
         * @memberOf Validator#
         * @type {boolean}
         * @private
         */
        self._isReady = true;
    });
};

Validator.EVENT_WILL_VALIDATE_FIELD = 'validator:willValidateField';
Validator.EVENT_FIELD_PASSED_VALIDATION = 'validator:fieldPassedValidation';
Validator.EVENT_FIELD_FAILED_VALIDATION = 'validator:fieldFailedValidation';
Validator.EVENT_FIELD_FIXED_VALIDATION = 'validator:fieldFixedValidation';

Validator.events = [
    Validator.EVENT_WILL_VALIDATE_FIELD, Validator.EVENT_FIELD_PASSED_VALIDATION,
    Validator.EVENT_FIELD_FAILED_VALIDATION, Validator.EVENT_FIELD_FIXED_VALIDATION
];

/**
 * Return true if the field contains a generic
 * collection index.
 * @public
 * @param {string} modelFieldName
 * @returns {boolean}
 */
Validator.isGenericFieldName = function (modelFieldName) {
    return modelFieldName.indexOf('[*]') > -1;
};

/**
 * Replace specific collection indicies with the
 * 'generic' index symbol '[*]'
 * @public
 * @param {string} modelFieldName
 * @returns {string}
 */
Validator.getGenericFieldName = function (modelFieldName) {
    return modelFieldName.replace(fieldRegex, '[*]');
};

/**
 * Return true if a field name has a specific collection
 * index (e.g. foo.bar[1].bam)
 * @public
 * @param {string} modelFieldName
 * @returns {boolean}
 */
Validator.hasCollectionIndex = function (modelFieldName) {
    return fieldRegex.test(modelFieldName);
};

Validator.prototype.initialize = function () {
    this.on(Validator.EVENT_WILL_VALIDATE_FIELD, this.onWillValidateField, this);
    this.on(Validator.EVENT_FIELD_PASSED_VALIDATION, this.onFieldPassed, this);
    this.on(Validator.EVENT_FIELD_FAILED_VALIDATION, this.onFieldFailed, this);
    this.on(Validator.EVENT_FIELD_FIXED_VALIDATION, this.onFieldFixed, this);
};

Validator.prototype.validationRules = null;

Validator.prototype.modelConstructor = null;

Validator.prototype.deferToChildValidators = true;

/**
 * Override this in an extension and
 * return a deferred promise if you want
 * to delay validation.
 * @public
 * @returns {null}
 */
Validator.prototype.getReadyPromise = function () {
    return null;
};

/**
 * @param {function} callback
 * @param {object} [context]
 * @public
 * @returns {Validator}
 */
Validator.prototype.onReady = function (callback, context) {
    when(this.getReadyPromise()).done(function () {
        callback.call(context);
    });
    return this;
};

/**
 * @param {function} callback
 * @param {object} [context]
 * @public
 * @returns {Validator}
 */
Validator.prototype.onReadyFailure = function (callback, context) {
    when(this.getReadyPromise()).fail(function () {
        callback.apply(context, arguments);
    });
    return this;
};

/**
 * @public
 * @returns {boolean}
 */
Validator.prototype.isReady = function () {
    //var readyProm = this.getReadyPromise();
    //return !readyProm || readyProm.state() === 'resolved';
    return !!this._isReady;
};

/**
 * @param {FieldValidation} fieldValidation
 * @returns {Object}
 */
Validator.prototype.getSchemaForField = function (fieldValidation) {
    return fieldValidation.outputSchema();
};

/**
 * Method to generate the full schema for a Validator. If your validator returns different schema
 * based on the values of a model, override this method so that documentation can be generated
 * @returns {Object}
 */
Validator.prototype.getFullSchema = function () {
    return { default: _.result(this, 'fieldValidations') };
};

/**
 * Add a "child" validator. The child validator can be used in
 * CollectionFieldValidation field validations or as a way to
 * delegate validation for a particular related model to another
 * validator. For example, if you have a "shipmentQuotes" child
 * validator to a parent validator, validating shipmentQuotes[0].foo
 * on the parent validator will simply defer to the child by
 * validating "foo" field with the first model in the shipment quotes
 * collection.
 * @public
 * @param {string} key
 *      A matching field name for a relation on the model
 * @param {Validator} childValidator
 */
Validator.prototype.setChildValidatorForKey = function (key, childValidator) {
    assert(childValidator instanceof Validator, 'childValidator must be an instance of Validator');
    this._childValidators[key] = childValidator;
    childValidator.parentValidator = this;
};

/**
 * @public
 * @param {string} key
 * @returns {FieldValidation}
 */
Validator.prototype.getChildValidator = function (key) {
    return this._childValidators[key];
};

/**
 * @public
 * @param {string} deferToChildValidators
 * @returns {Validator}
 */
Validator.prototype.setDeferToChildValidators = function (deferToChildValidators) {
    this.deferToChildValidators = deferToChildValidators;
    return this;
};

/**
 * @protected
 * @param {string} key
 */
Validator.prototype.removeChildValidator = function (key) {
    var childValidator = this._childValidators[key];
    if (!childValidator) {
        return;
    }
    childValidator.parentValidator = null;
    delete this._childValidators[key];
};

/**
 * @protected
 * Receives following params
 * - {string} fieldName
 * - {FieldValidation} fieldValidation
 */
Validator.prototype.onWillValidateField = function (fieldName) {
    this._lastValidatedFields.push(fieldName);
};

/**
 * Receives following params
 * - {string} fieldName
 * - {FieldValidation} fieldValidation
 * @protected
 */
Validator.prototype.onFieldPassed = function (fieldName, fieldValidation) {
    var errorIndex;
    this._lastPassedFields.push(fieldName);
    errorIndex = _.indexOf(this._fieldsWithErrors || [], fieldName);
    if (errorIndex > -1) {
        this._fieldsWithErrors.splice(errorIndex, 1);
        this.triggerFixedValidation(fieldName, fieldValidation);
    }
};

/**
 * Receives following params
 * - {string} fieldName
 * - {FieldValidation} fieldValidation
 * - {object[{ field: string, message: string }]} errors
 * @protected
 */
Validator.prototype.onFieldFailed = function (fieldName) {
    this._lastFailedFields.push(fieldName);
    if (_.indexOf(this._fieldsWithErrors, fieldName) < 0) {
        this._fieldsWithErrors.push(fieldName);
    }
};

/**
 * Receives following params
 * - {string} fieldName
 * - {FieldValidation} fieldValidation
 * @protected
 */
Validator.prototype.onFieldFixed = function (fieldName) {
    this._lastFixedFields.push(fieldName);
};

/**
 * Returns a list of the model fields that were
 * validated on the last 'validate' method call
 * @returns {string[]}
 * @public
 */
Validator.prototype.getLastValidatedFields = function () {
    return this._lastValidatedFields;
};

/**
 * Returns a list of the model fields that passed
 * validation on the last 'validate' method call
 * @returns {string[]}
 * @public
 */
Validator.prototype.getLastPassedFields = function () {
    return this._lastPassedFields;
};

/**
 * Returns a list of the model fields that failed
 * validation on the last 'validate' method call
 * @returns {string[]}
 * @public
 */
Validator.prototype.getLastFailedFields = function () {
    return this._lastFailedFields;
};

/**
 * Returns a list of the model fields that were fixed
 * on the last 'validate' method call
 * @returns {string[]}
 * @public
 */
Validator.prototype.getLastFixedFields = function () {
    return this._lastFixedFields;
};

/**
 * @param {string} modelFieldName
 * @returns {boolean}
 */
Validator.prototype.hasCollectionIndex = function (modelFieldName) {
    return Validator.hasCollectionIndex(modelFieldName);
};

/**
 * Return true if the field
 * @public
 * @param modelFieldName
 * @returns {boolean}
 */
Validator.prototype.isGenericFieldName = function (modelFieldName) {
    return Validator.isGenericFieldName(modelFieldName);
};

/**
 * @public
 * @param {string} modelFieldName
 * @returns {string}
 */
Validator.prototype.getGenericFieldName = function (modelFieldName) {
    return Validator.getGenericFieldName(modelFieldName);
};

/**
 * @param {string} genericModelFieldName
 * @returns {boolean}
 * @private
 */
Validator.prototype._genericFieldHasError = function (genericModelFieldName) {
    var i = 0;
    for (i; i < this._fieldsWithErrors.length; i++) {
        if (this.getGenericFieldName(this._fieldsWithErrors[i]) === genericModelFieldName) {
            return true;
        }
    }
    return false;
};

/**
 * If a field has an error, based on result of
 * last "validate" call, return true. Otherwise,
 * return false.
 * @public
 * @param {string} modelFieldName
 * @returns {boolean}
 */
Validator.prototype.fieldHasError = function (modelFieldName) {
    var i = 0;
    if (this.isGenericFieldName(modelFieldName)) {
        return this._genericFieldHasError(modelFieldName);
    }
    for (i; i < this._fieldsWithErrors.length; i++) {
        if (this._fieldsWithErrors[i] === modelFieldName) {
            return true;
        }
    }
    return false;
};

/**
 * Resets the arrays used to track the model fields
 * that were validated and passed or failed validation
 * the last time 'validate' was called.
 * @private
 */
Validator.prototype._resetValidationTrackers = function () {
    if (!this._fieldsWithErrors) {
        this._fieldsWithErrors = [];
    }
    this._lastValidatedFields = [];
    this._lastPassedFields = [];
    this._lastFailedFields = [];
    this._lastFixedFields = [];
    this._childValidatorsFieldBuffers = {};
};

/**
 * @param {string} modelFieldName
 * @param {FieldValidation} fieldValidation
 */
Validator.prototype.triggerWillValidateField = function (modelFieldName, fieldValidation) {
    this.trigger(Validator.EVENT_WILL_VALIDATE_FIELD, modelFieldName, fieldValidation);
};

/**
 * @param {string} modelFieldName
 * @param {FieldValidation} fieldValidation
 */
Validator.prototype.triggerFieldPassed = function (modelFieldName, fieldValidation) {
    this.trigger(Validator.EVENT_FIELD_PASSED_VALIDATION, modelFieldName, fieldValidation);
};

/**
 * @param {string} modelFieldName
 * @param {FieldValidation} fieldValidation
 */
Validator.prototype.triggerFixedValidation = function (modelFieldName, fieldValidation) {
    this.trigger(Validator.EVENT_FIELD_FIXED_VALIDATION, modelFieldName, fieldValidation);
};

/**
 * @param {string} modelFieldName
 * @param {FieldValidation} fieldValidation
 * @param {object[]} errors
 */
Validator.prototype.triggerFieldFailed = function (modelFieldName, fieldValidation, errors) {
    this.trigger(Validator.EVENT_FIELD_FAILED_VALIDATION, modelFieldName, fieldValidation, errors);
};

/**
 * @public
 * @param {string} fieldName The field being validated
 * @returns {string} The matching child validator if there is one
 */
Validator.prototype.fieldMatchesChildValidator = function (fieldName) {
    var children = this._childValidators;
    var childValidatorKey;
    for (childValidatorKey in children) {
        if (children.hasOwnProperty(childValidatorKey)) {
            if (fieldName.indexOf(childValidatorKey) === 0 &&
                    _.indexOf([".", "["], fieldName.charAt(childValidatorKey.length)) > -1) {
                return childValidatorKey;
            }
        }
    }
    return null;
};

/**
 * @param {string} modelFieldName
 * @param {string} childValidatorKey
 * @private
 */
Validator.prototype._shouldValidateFieldWithChildValidator = function (modelFieldName, childValidatorKey) {
    // converts 'foo.bar[1]' into 'foo\.bar\[1\]'
    var regexKeySeg = childValidatorKey.replace(/\[\.\]/g, function (val) {
        return '\\' + val;
    });
    // breaks foo.bar into ['foo.bar', undefined, 'bar'] and foo[0].bar into ['foo[0].bar', '[0]', 'bar']
    var fieldDataRegex = new RegExp('^' + regexKeySeg + '(?:(?:(\\[\\d+\\])\\.)|\\.)(.+)');
    var matches = modelFieldName.match(fieldDataRegex);
    var childValidatorBuffer;
    var modelPath;
    if (matches && matches.length) {
        modelPath = childValidatorKey + (matches[1] || '');
        // We need to know the path to the model, the full field path (including parent model),
        // and the field relative to the model
        if (!this._childValidatorsFieldBuffers[modelPath]) {
            this._childValidatorsFieldBuffers[modelPath] = {
                childValidatorKey: childValidatorKey,
                fields: [],
                fullFieldPaths: []
            };
        }
        childValidatorBuffer = this._childValidatorsFieldBuffers[modelPath];
        childValidatorBuffer.fields.push(matches[2]);
        childValidatorBuffer.fullFieldPaths.push(modelFieldName);
    }
};

/**
 * @param {ErrorList} errorsList
 * @private
 */
Validator.prototype._validateChildValidatorFields = function (errorsList) {
    _.each(this._childValidatorsFieldBuffers, function (buffer, pathToModel) {
        var validator = this.getChildValidator(buffer.childValidatorKey);
        var childValidatorOut;
        var childModel = pathToModel ? this.model.get(pathToModel) : this.model;
        var i = 0;
        var err;
        this._matchChildValidatorEvents(pathToModel);
        childValidatorOut = validator.validate(childModel, { fields: buffer.fields });
        this._matchChildValidatorEvents(pathToModel, true);
        for (i; i < childValidatorOut.errors.length; i++) {
            err = childValidatorOut.errors[i];
            errorsList.add(buffer.fullFieldPaths[_.indexOf(buffer.fields, err.field)], err.message, err.rule);
        }
    }, this);
    return errorsList;
};

/**
 * Set up / removes events on the validator instance that
 * mirror the same events on child validators.
 * @param {string} pathToModel
 * @param {boolean} [removeEvents=false]
 * @private
 */
Validator.prototype._matchChildValidatorEvents = function (pathToModel, removeEvents) {
    var self = this;
    var fieldPrefix = pathToModel ? pathToModel + '.' : '';
    var events = Validator.events;
    var getCallback = function (event) {
        return function () {
            arguments[0] = fieldPrefix + arguments[0];
            self.trigger.apply(self, [event].concat(_.toArray(arguments)));
        };
    };
    _.each(self._childValidators, function (childValidator) {
        var i = 0;
        for (i; i < events.length; i++) {
            childValidator.off(events[i], null, self);
            if (!removeEvents) {
                childValidator.on(events[i], getCallback(events[i]), self);
            }
        }
    });
};


/**
 * @param {boolean} requireNone
 * @protected
 */
Validator.prototype.setRequireNone = function (requireNone) {
    this._requireNone = requireNone;
    this._setChildrenRequireNone(requireNone);
};

/**
 * @param {boolean} requireNone
 * @private
 */
Validator.prototype._setChildrenRequireNone = function (requireNone) {
    _.each(this._childValidators, function (validator) {
        validator.setRequireNone(requireNone);
    });
};

/**
 * Extend the Validator class like you would a Backbone.Model
 * @function
 * @memberOf Validator
 * @param {object} [instanceProperties]
 * @param {object} [staticProperties]
 * @returns {function(new:Validator)} A new Validator constructor
 */
Validator.extend = Backbone.Model.extend;

// Give Validator instances the ability to listen to and fire events
_.extend(Validator.prototype, Backbone.Events);

Validator.prototype.validate = null;

/*eslint-disable*/
// Disabling eslint errors (var must be at top of file) as this is deprecated and shouldn't need to be maintained for much longer, (hopefully)

/**
 * Helper function does individual validations.
 * @param {object} rule
 * @param {boolean} [rule.noMatch=false]
 * @param {function|RegExp} [rule.validator]
 * @param {string} fieldName exp. creators[0].name
 *      - validator <RegExp | Function>
 * @param value
 * @returns {boolean}
 * @private
 */
var _validateSingle = function (rule, value, fieldName, required) {
    var self = this;
    var test = true;
    var func = rule.validator;

    // Throws an error if validator is improperly configured. Each rules property of a validaiton config must
    // have a validator property
    if (!func || !(func instanceof RegExp || func instanceof Function)) {
        throw new Error("Invalid validator configuration. Each validation config's rules property must have " +
            "a validator method which is an instance of RegExp or Function");
    }

    if (func instanceof RegExp) {
        test = func.test(value);
    } else if (typeof func === 'function') {
        test = func.call(self, value, fieldName, required);
    }

    // for nomatch rules, invert the answer :
    if (rule.noMatch) {
        test = !test;
    }

    // If the error is not either bool or instance of ErrorList then error out.
    if (!_.isBoolean(test) && !(test instanceof ErrorList)) {
        throw new Error("Validator for '" + fieldName + "' returned an invalid result.");
    }

    return test;
};

var _getNestedValue = function (model, string) {
    var obj = model.toJSON();
    string = string.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
    string = string.replace(/^\./, ''); // strip a leading dot
    var arr = string.split('.');
    var n;
    while (arr.length) {
        n = arr.shift();
        if (obj.hasOwnProperty(n)) {
            obj = obj[n];
        } else {
            return;
        }
    }
    return obj;
};

/**
 * Helper function to do validations based on a validation schema.
 * @param {object} fieldValidations - validation schema object (rules)
 * @param {object} options - fields not to evaluate for required rule if passed
 * @returns {{ errors: { field:string, message: string }[] }}
 */
var _validate = function (fieldValidations, options) {
    options = options || {};

    this._resetValidationTrackers();

    var model = this.model;
    var out = {};
    var errorList = new ErrorList();
    var fieldsToValidate = [];
    var topLevelFields = [];
    var schema = {};
    var requireNone = _.isBoolean(options.requireNone) ? options.requireNone : this._requireNone;
    var specificFieldsToValidate = options.partial !== true && options.fields ? options.fields.slice(0) : null; // null out fields option if partial is true
    assert(specificFieldsToValidate === null || specificFieldsToValidate instanceof Array, 'options.fields must be an array');

    // Make a deep clone of the schema -- this way if we alter the schema rules during the course of this method, the
    // core set will not be affected :
    // schema = clone(schema);

    // Get the list of fields that to validate.
    // By default, the fields to validate is all auto-run fields :
    //fieldsToValidate = _.pluck(_.values(_.where(schema, {autoRun : true})), 'field');

    _.each(fieldValidations, function (fieldValidation, fieldValidationKey) {
        console.assert(fieldValidation instanceof FieldValidation, 'fieldValidation must be an instance of FieldValidation');
        var fieldSchema = schema[fieldValidationKey] = this.getSchemaForField(fieldValidation, fieldValidationKey);
        assert(typeof fieldSchema.field === 'string', 'The fieldValidation for key ' + fieldValidationKey + ' must define a "field"');
        if (!specificFieldsToValidate && fieldSchema.autoRun) {
            fieldsToValidate.push(fieldSchema.field);
        }
    }, this);
    if (specificFieldsToValidate) {
        fieldsToValidate = specificFieldsToValidate;
    }

    // Otherwise, if the client is asking to validate the partial object, over-write fields to validate with any
    // field where the top level attribute was passed in (1st segment in field name) :
    if (options.partial === true) {
        topLevelFields = _.keys(options.payload);
        var newFieldsToValidate = [];
        _.each(fieldsToValidate, function (fieldName) {
            var furstSegment = _.first(fieldName.split('.'));
            if (_.indexOf(topLevelFields, furstSegment) !== -1) {
                newFieldsToValidate.push(fieldName);
            }
        });
        fieldsToValidate = newFieldsToValidate;
    }

    // Next, apply the require none option. Toggle all required fields in the schema to not required. This will be
    // over-ridden below by individual field overrides.
    if (requireNone) {
        this._setChildrenRequireNone(true);
        _.each(schema, function (config) {
            config.required = false;
        });
    }

    // Loop over every field that you are going to validate. For each look up the associated validator from the
    // validation schema :
    _.each(fieldsToValidate, function (fieldName) {
        var fieldValidation;
        var fieldValidationKey;
        var fieldValidationSchema;
        var genericFieldName = fieldName;
        var fieldErrors = [];
        var childValidatorKey;
        var val;
        var required;
        var hasRealValue;
        var message;
        var override;

        // Throws an error if no field name is configured on validation config.
        if (!fieldName) {
            throw new Error('Invalid configuration. Each validation config item must have a field property.');
        }

        if (this.deferToChildValidators) {
            // If there is a child validator matching this field and
            // deferToChildValidators is true
            childValidatorKey = this.fieldMatchesChildValidator(fieldName);
            if (childValidatorKey) {
                this._shouldValidateFieldWithChildValidator(fieldName, childValidatorKey);
                return;
            }
        }

        // If the field regex detects a number (ie. "abc[1].def") -- then replace the number with a * to achieve a
        // generic field name.
        if (fieldRegex.test(fieldName)) {
            genericFieldName = fieldName.replace(fieldRegex, '[*]');
        }

        // Find validation onfigs -- the first config that matches the given field name :
        // Note *** If many validation configs are configured to match a given field, then only the first one will
        // run, you may not define many configs to match against a certain field name.
        for (fieldValidationKey in schema) {
            if (schema.hasOwnProperty(fieldValidationKey) && (genericFieldName === schema[fieldValidationKey].field)) {
                fieldValidation = fieldValidations[fieldValidationKey];
                fieldValidationSchema = schema[fieldValidationKey];
                break;
            }
        }
        if (!fieldValidationSchema) {
            return;
        }

        // If there are any over-rides for this validation config, then apply them now. Note* This reverses the blanket
        // rule of "requireNone" for specified fields.
        if (options.overrides) {
            override = _.find(options.overrides, function (o) {
                var field = o.field || o.fieldName;
                return (fieldValidationSchema.field === field);
            });
            if (override) {
                // TODO ::: ONLY EXTEND ON OVER-RIDES THAT WOULD NOT BREAK, ie. DO NOT APPLY OVER RIDES THAT ARE rules
                // TODO ::: BECAUSE IF PASSED IN AS STRING -- FUNCTIONS WOULD BLOW UP! (use pick / omit?)
                fieldValidationSchema = _.extend({}, fieldValidationSchema, override);
            }
        }

        // We know the specific model field and are about to validate it
        // so trigger an event so any listeners can handle it appropriately
        this.triggerWillValidateField(fieldName, fieldValidation);

        // Now get the value you are to evaluate :
        try {
            val = (fieldValidation.getModelValueForField) ?
                    fieldValidation.getModelValueForField(model, fieldName) : model.get(fieldName);
        } catch (e) {
            val = _getNestedValue(model, fieldName);
        }

        // If the value is an array, then check the length :
        if (_.isArray(val)) {
            val = val.length ? val : false;
        }

        if (_.isBoolean(fieldValidationSchema.required)) {
            required = fieldValidationSchema.required;
        } else if (_.isFunction(fieldValidationSchema.required)) {
            required = fieldValidationSchema.required(model, val, fieldName);
        } else {
            required = false;
        }

        // If the current field is required, then validate it has a value :
        hasRealValue = fieldValidation.hasRealValue ?
                fieldValidation.hasRealValue(model, val, fieldName) : (parseInt(val, 10) === 0 || !!val);

        if (required && !hasRealValue) {
            message = (fieldValidationSchema.messages && fieldValidationSchema.messages.required) ?
                    fieldValidationSchema.messages.required : DEFAULTREQUIREDMESSAGE;
            errorList.add(fieldValidationSchema.field, message, "required");

            this.triggerFieldFailed(fieldName, fieldValidation, [errorList.last()]);
            // Return from this case and do not run other validations, since the item is required and is NOT defined.
            return;
        }

        // Next, if the value is set to something AND there are rules defined...
        // apply each of the rules to the current field, getting the output of each :
        if (fieldValidationSchema.rules && hasRealValue) {
            _.each(fieldValidationSchema.rules, function (validator, key) {

                // Get the result for the current validator. Results can be boolean true | false OR they can be
                // objects describing an error.
                var result = _validateSingle.call(model, validator, val, fieldName, required);
                var currMessage = (fieldValidationSchema.messages && fieldValidationSchema.messages[key]) ? fieldValidationSchema.messages[key] : DEFAULT_MESSAGE + fieldName;

                // If the result is a boolean add the default error based on the field being validated. Otherwise
                // the result should be an array of errors from the ErrorList Class
                if (_.isBoolean(result) && !result) {
                    errorList.add(fieldName, currMessage, validator.validator);
                    fieldErrors.push(errorList.last());
                // If the result is an ErrorList -- then merge it with the overall error list.
                } else if (result instanceof ErrorList && result.getErrors().length) {
                    errorList.merge(result);
                    fieldErrors = fieldErrors.concat(errorList.getErrors());
                }

            }, this);

            if (fieldErrors.length) {
                this.triggerFieldFailed(fieldName, fieldValidation, fieldErrors);
            } else {
                this.triggerFieldPassed(fieldName, fieldValidation);
            }

        }
    }, this);

    this._validateChildValidatorFields(errorList);

    if (requireNone) {
        this._setChildrenRequireNone(false); // restore requireNone's default value (false);
    }

    out.errors = errorList.getErrors();

    return out;
};

/**
 * Do validation on a plain JS object, configured by options.
 *
 * @param {object|array} payload JSON object to be validated.
 * @param {object} [options] - Options to configure validation. May contain any of the following :
 * @param {boolean} [options.partial=false] Defaults to false. IF passed as true then only validate the fields passed in (present on the payload)
 * @param {string[]} [options.fields] Array of strings, if passed and not empty then ONLY the fields specified will be validated.
 * @param {boolean} [options.requireNone=false] Defaults to false. Don't evaluate any of the fields to be required. ***Note : you may set this to true, then toggle selected fields to be required by using the overrides array.
 * @param {object[]} [options.overrides] Over-ride the values for validations in the validation schema for this entity. This is an array of objects that mimics the structure of the vdalidation schemas.
 * @returns {{ errors: object[] }}
 */
Validator.prototype.validate = function (payload, options) {

    options = options || {};
    _.extend(options, {
        payload : payload
    });

    var self = this;
    var requiredArr = ['modelConstructor', 'fieldValidations'];
    var changeError = function () {
        throw new Error('Validator: model should not change during validation');
    };
    var validationRules;
    var output;

    assert(this.isReady(), 'Validator instance is not ready to validate yet.');

    // Check the required properties are set on the validation instance :
    _.each(requiredArr, function (required) {
        if (!self[required]) {
            throw new Error('Missing required property : ' + required);
        }
    });

    assert(payload instanceof this.modelConstructor || payload instanceof Backbone.Model === false,
        'Validator.validate payload must be instance of modelConstructor or toJSON output');
    this.model = (payload instanceof this.modelConstructor) ? payload : new this.modelConstructor(payload);

    this.model.on('nested-change', changeError).on('change', changeError);

    // If validation rules is a function, then execute that function to get the rules set :
    validationRules = this.fieldValidations;
    if (typeof this.fieldValidations === 'function') {
        validationRules = this.fieldValidations(this.model, options);
    }

    // run validations in the context of this validator instance, and return the results
    output = _validate.call(this, validationRules, options);

    // remove the event handler
    this.model.off('nested-change', changeError).off('change', changeError);

    return output;
};
/*eslint-enable*/
module.exports = Validator;



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/validation/deprecated/Validator.js
// module id = 121
// module chunks = 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31 32 33 34 35 36 37 38 39 40 41 42 43 44 45 46 47 48 49 50 51 52 53 54 55